import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, AlertCircle, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useBillingAndUsage';

export const SubscriptionBanner: React.FC = () => {
  const { data: subscription } = useSubscription();
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check session storage for dismissed warning banner
    const dismissed = sessionStorage.getItem('subscription_banner_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  if (!subscription) return null;

  const now = Date.now();
  const status = subscription.status;
  const trialTime = subscription.trial_ends_at
    ? new Date(subscription.trial_ends_at).getTime()
    : null;
  const graceTime = subscription.grace_ends_at
    ? new Date(subscription.grace_ends_at).getTime()
    : null;

  const isTrialExpired = status === 'trialing' && trialTime !== null && trialTime <= now;
  const trialDaysLeft =
    trialTime !== null && trialTime > now
      ? Math.ceil((trialTime - now) / (1000 * 60 * 60 * 24))
      : 0;

  const isPastDueExpired =
    status === 'past_due' && (graceTime === null || graceTime <= now);
  const graceDaysLeft =
    graceTime !== null && graceTime > now
      ? Math.ceil((graceTime - now) / (1000 * 60 * 60 * 24))
      : 0;

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('subscription_banner_dismissed', 'true');
  };

  // 1. Trial Expired Banner (Hard Restriction - Non-dismissible)
  if (isTrialExpired) {
    return (
      <div className="relative isolate flex items-center justify-between gap-x-4 bg-red-600 px-6 py-2.5 sm:px-8 text-white shadow-md">
        <div className="flex items-center gap-x-3 text-sm leading-6">
          <AlertTriangle className="h-5 w-5 shrink-0 text-white animate-pulse" />
          <p>
            <strong className="font-semibold">Free Trial Expired:</strong> Your 14-day trial has
            ended. Adding new influencers, campaigns, and team members is restricted until you
            upgrade.
          </p>
        </div>
        <div className="flex items-center gap-x-3 shrink-0">
          <Button
            size="sm"
            onClick={() => navigate('/settings?tab=billing&upgrade=Starter')}
            className="bg-white text-red-700 hover:bg-red-50 font-semibold shadow-sm"
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5 text-red-600" />
            Upgrade Plan
          </Button>
        </div>
      </div>
    );
  }

  // 2. Past Due Expired Banner (Non-dismissible)
  if (isPastDueExpired) {
    return (
      <div className="relative isolate flex items-center justify-between gap-x-4 bg-red-600 px-6 py-2.5 sm:px-8 text-white shadow-md">
        <div className="flex items-center gap-x-3 text-sm leading-6">
          <AlertCircle className="h-5 w-5 shrink-0 text-white" />
          <p>
            <strong className="font-semibold">Payment Past Due:</strong> Your grace period has ended.
            Please update your payment method to restore account capabilities.
          </p>
        </div>
        <div className="flex items-center gap-x-3 shrink-0">
          <Button
            size="sm"
            onClick={() => navigate('/settings?tab=billing')}
            className="bg-white text-red-700 hover:bg-red-50 font-semibold shadow-sm"
          >
            Update Billing
          </Button>
        </div>
      </div>
    );
  }

  // 3. Past Due In Grace Period Banner (Non-dismissible)
  if (status === 'past_due' && graceDaysLeft > 0) {
    return (
      <div className="relative isolate flex items-center justify-between gap-x-4 bg-amber-600 px-6 py-2.5 sm:px-8 text-white shadow-md">
        <div className="flex items-center gap-x-3 text-sm leading-6">
          <Clock className="h-5 w-5 shrink-0 text-white" />
          <p>
            <strong className="font-semibold">Payment Notice:</strong> Your payment is past due. You
            have{' '}
            <strong>
              {graceDaysLeft} {graceDaysLeft === 1 ? 'day' : 'days'}
            </strong>{' '}
            remaining in your grace period.
          </p>
        </div>
        <div className="flex items-center gap-x-3 shrink-0">
          <Button
            size="sm"
            onClick={() => navigate('/settings?tab=billing')}
            className="bg-white text-amber-800 hover:bg-amber-50 font-semibold shadow-sm"
          >
            Update Billing
          </Button>
        </div>
      </div>
    );
  }

  // 4. Trial Approaching Expiration (<= 3 Days Left - Dismissible for session)
  if (status === 'trialing' && trialDaysLeft > 0 && trialDaysLeft <= 3 && !isDismissed) {
    return (
      <div className="relative isolate flex items-center justify-between gap-x-4 bg-gradient-to-r from-purple-700 to-indigo-700 px-6 py-2.5 sm:px-8 text-white shadow-sm">
        <div className="flex items-center gap-x-3 text-sm leading-6">
          <Clock className="h-5 w-5 shrink-0 text-amber-300" />
          <p>
            <strong className="font-semibold">Free Trial:</strong> Your 14-day trial ends in{' '}
            <span className="font-bold text-amber-300">
              {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'}
            </span>
            . Upgrade now to preserve full uninterrupted access.
          </p>
        </div>
        <div className="flex items-center gap-x-2 shrink-0">
          <Button
            size="sm"
            onClick={() => navigate('/settings?tab=billing&upgrade=Starter')}
            className="bg-white text-purple-800 hover:bg-purple-50 font-semibold shadow-sm"
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5 text-purple-600" />
            View Plans
          </Button>
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-lg p-1 text-purple-200 hover:bg-purple-600/50 hover:text-white transition-colors"
            title="Dismiss for session"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default SubscriptionBanner;
